import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, ScanLine, Bell, Upload, LogOut, Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { cn } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Logo } from '../brand';
import { useUploadProgress } from '../lib/uploadProgress';
import {
    LayoutDashboard,
    Receipt,
    BookOpen,
    Package,
    Store,
    Users,
    Wallet,
    FileSpreadsheet,
    FileText,
    ShieldCheck,
    ScanText,
    Milk,
    Settings as SettingsIcon
} from 'lucide-react';

const NAV = [
    { to: '/', label: 'Dashboard', icon: LayoutDashboard, testid: 'mobile-nav-dashboard', end: true },
    { to: '/upload', label: 'Upload', icon: Upload, testid: 'mobile-nav-upload' },
    { to: '/bills', label: 'Bills', icon: Receipt, testid: 'mobile-nav-bills' },
    { to: '/library', label: 'Library', icon: BookOpen, testid: 'mobile-nav-library', badge: 'New' },
    { to: '/items', label: 'Items', icon: Package, testid: 'mobile-nav-items' },
    { to: '/vendors', label: 'Vendors', icon: Store, testid: 'mobile-nav-vendors' },
    { to: '/staff', label: 'Staff', icon: Users, testid: 'mobile-nav-staff' },
    // 2026-01 fix: Expenses was accidentally omitted from the mobile menu
    // even though it exists in the desktop sidebar. Restored so mobile users
    // can access the Expenses tab (universal expense tracking + salary payments).
    { to: '/expenses', label: 'Expenses', icon: Wallet, testid: 'mobile-nav-expenses' },
    { to: '/vouchers', label: 'Vouchers', icon: Receipt, testid: 'mobile-nav-vouchers', badge: 'New' },
    // 2026-07 fix: Invoicing (Phase 19) module was missing from mobile menu,
    // causing users to think the feature wasn't deployed. Now mirrors the
    // desktop Sidebar entry (with "New" badge for discoverability).
    { to: '/invoicing', label: 'Invoicing', icon: FileText, testid: 'mobile-nav-invoicing', badge: 'New' },
    { to: '/alerts', label: 'Alerts', icon: Bell, testid: 'mobile-nav-alerts' },
    { to: '/ca-export', label: 'Export', icon: FileSpreadsheet, testid: 'mobile-nav-ca-export' },
    { to: '/settings', label: 'Settings', icon: SettingsIcon, testid: 'mobile-nav-settings' }
];

export function Topbar({ alertsCount = 0, scannerEnabled = false }) {
    const [open, setOpen] = useState(false);
    const nav = useNavigate();
    const { user, logout } = useAuth();
    // Sprint 2026-07 (A.2): live upload progress from anywhere in the app.
    // When bills are being processed on /upload, we mirror progress here so
    // the user can navigate away and still see it in the top bar.
    const uploadProgress = useUploadProgress();
    const uploadActive = (uploadProgress?.active || 0) + (uploadProgress?.queued || 0) > 0;
    const uploadDoneCount = (uploadProgress?.done || 0) + (uploadProgress?.duplicate || 0) + (uploadProgress?.error || 0);
    const uploadTotal = uploadProgress?.total || 0;
    useEffect(() => setOpen(false), []);

    // Sprint 2026-07: mirror desktop Sidebar's split probes for mobile too.
    //   • scannerWhoami → Daily Sheet + Vijay Ras entries (larger allowlist).
    //   • ownerWhoami   → Owner super-admin dashboard (single OWNER_EMAIL).
    const [isOwner, setIsOwner] = useState(false);
    const [isScannerUser, setIsScannerUser] = useState(false);
    useEffect(() => {
        let cancelled = false;
        api.ownerWhoami()
            .then(() => { if (!cancelled) setIsOwner(true); })
            .catch(() => { if (!cancelled) setIsOwner(false); });
        api.scannerWhoami()
            .then(() => { if (!cancelled) setIsScannerUser(true); })
            .catch(() => { if (!cancelled) setIsScannerUser(false); });
        return () => { cancelled = true; };
    }, [user?.email]);

    // Sprint C: hide alerts nav + bell when master toggle is off (App.js
    // passes alertsCount === null in that case).
    const alertsHidden = alertsCount === null;
    let visibleNav = alertsHidden ? NAV.filter((n) => n.to !== '/alerts') : NAV;
    if (isScannerUser && scannerEnabled) {
        visibleNav = [
            ...visibleNav,
            // Personal handwritten-sheet scanner + Vijay Ras (father's store)
            // monthly-quantity ledger. Gated by OWNER_SCANNER_EMAILS
            // allowlist, so no un-invited tenant will ever see these.
            { to: '/daily-sheet', label: 'Daily Sheet', icon: ScanText, testid: 'mobile-nav-daily-sheet' },
            { to: '/vijay-ras', label: 'Vijay Ras', icon: Milk, testid: 'mobile-nav-vijay-ras' },
        ];
    }
    if (isOwner) {
        visibleNav = [
            ...visibleNav,
            { to: '/owner', label: 'Owner', icon: ShieldCheck, testid: 'mobile-nav-owner' },
        ];
    }
    return (
        <header
            data-testid="topbar"
            className="sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border/80 bg-background/85 backdrop-blur px-4 sm:px-6 lg:px-8 h-14"
        >
            <div className="flex items-center gap-3 md:hidden">
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Open menu" data-testid="topbar-menu-button">
                            <Menu size={20} />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="left" className="w-72 p-0 flex flex-col">
                        <div className="flex items-center gap-2.5 px-5 py-4 border-b">
                            <Logo variant="icon" size={38} data-testid="brand-logo-topbar-drawer" />
                            <div className="min-w-0">
                                <div className="font-heading text-lg leading-tight">Ease My Bill</div>
                                <div className="text-[11px] text-muted-foreground">Snap. Scan. Save.</div>
                            </div>
                        </div>
                        <nav className="px-3 py-3 space-y-1">
                            {visibleNav.map((n) => (
                                <NavLink
                                    key={n.to}
                                    to={n.to}
                                    end={n.end}
                                    onClick={() => setOpen(false)}
                                    data-testid={n.testid}
                                    className={({ isActive }) =>
                                        cn(
                                            'group flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60',
                                            isActive && 'text-foreground bg-muted/80 border-l-2 border-primary'
                                        )
                                    }
                                >
                                    <n.icon size={18} />
                                    <span className="flex-1">{n.label}</span>
                                    {n.badge && (
                                        <span className="inline-flex items-center justify-center px-1.5 py-0 rounded text-[9px] font-semibold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                                            {n.badge}
                                        </span>
                                    )}
                                </NavLink>
                            ))}
                        </nav>
                        <div className="mt-auto px-3 py-3 border-t space-y-1">
                            {user?.email && (
                                <div className="px-3 py-1.5 text-xs text-muted-foreground truncate">
                                    Signed in as <span className="text-foreground">{user.email}</span>
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    logout();
                                }}
                                data-testid="mobile-nav-logout"
                                className="w-full flex items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60"
                            >
                                <LogOut size={18} />
                                <span>Log out</span>
                            </button>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>

            <div className="flex md:hidden items-center gap-2">
                <Logo variant="icon" size={30} data-testid="brand-logo-topbar-mobile" />
                <div className="font-heading text-base">Ease My Bill</div>
            </div>

            <div className="hidden md:block flex-1" />

            <div className="flex items-center gap-2">
                {/* Sprint 2026-07 (A.2): live upload progress badge. Rendered
                    only while there are bills in flight. Clicking jumps
                    straight to /upload so the user can see per-item detail. */}
                {uploadActive && (
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 h-9 px-2.5 border-primary/30 bg-primary/5 hover:bg-primary/10"
                        onClick={() => nav('/upload')}
                        data-testid="topbar-upload-progress"
                        title={`Uploading ${uploadDoneCount} of ${uploadTotal} bills`}
                    >
                        <Loader2 size={14} className="animate-spin text-primary" />
                        <span className="text-xs font-mono tabular-nums">
                            {uploadDoneCount}/{uploadTotal}
                        </span>
                    </Button>
                )}
                {!alertsHidden && (
                    <Button
                        variant="ghost"
                        size="icon"
                        className="relative"
                        aria-label="Alerts"
                        data-testid="topbar-alerts-button"
                        onClick={() => nav('/alerts')}
                    >
                        <Bell size={18} />
                        {alertsCount > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[hsl(var(--price-increase-fg))] text-[10px] text-white flex items-center justify-center px-1 font-mono tabular-nums">
                                {alertsCount}
                            </span>
                        )}
                    </Button>
                )}
                <Button
                    data-testid="topbar-upload-button"
                    className="gap-2"
                    onClick={() => nav('/upload')}
                >
                    <Upload size={16} />
                    Upload Bill
                </Button>
            </div>
        </header>
    );
}
